import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { AdminApiService } from '../admin-api.service';
import { Permission, Role } from '../models';

interface ActionEntry {
  key: string;              // e.g. "lab:patients.read"
  action: string;           // e.g. "read" / "approve" / "" (for system:*)
  displayNameAr?: string;
  displayNameEn?: string;
  selected: boolean;
}

interface ResourceGroup {
  system: string;
  resource: string;
  label: string;
  icon: string;
  actions: ActionEntry[];
  expanded: boolean;
  matched: boolean;
}

@Component({
  standalone: true,
  selector: 'app-role-permissions-page',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    ToastModule,
    TooltipModule,
    TagModule,
  ],
  providers: [MessageService],
  styleUrls: ['./role-permissions.page.scss'],
  templateUrl: './role-permissions.page.html',
})
export class RolePermissionsPage implements OnInit {
  private api = inject(AdminApiService);
  private ms = inject(MessageService);
  private route = inject(ActivatedRoute);
  router = inject(Router);

  roleId: string | null = null;
  role: Role | null = null;

  loading = true;
  saving = false;
  search = '';
  systemFilter = '';

  systems: string[] = [];
  groups: ResourceGroup[] = [];

  initiallySelected = new Set<string>();

  // verb → semantic class (mirrors permissions page)
  private static readonly VERB_TONE: Record<string, string> = {
    read: 'tone-info',
    view: 'tone-info',
    list: 'tone-info',
    write: 'tone-success',
    create: 'tone-success',
    update: 'tone-warn',
    edit: 'tone-warn',
    delete: 'tone-danger',
    remove: 'tone-danger',
    approve: 'tone-success',
    reject: 'tone-danger',
    'pre-approve': 'tone-success',
    'pre-reject': 'tone-danger',
    accept: 'tone-success',
    invoice: 'tone-purple',
    transfer: 'tone-purple',
    deposit: 'tone-success',
    withdraw: 'tone-warn',
    collect: 'tone-success',
    send: 'tone-info',
    'send-bulk': 'tone-info',
    print: 'tone-neutral',
    export: 'tone-neutral',
  };

  // resource → icon
  private static readonly RESOURCE_ICON: Record<string, string> = {
    '*': 'pi pi-star-fill',
    access: 'pi pi-key',
    users: 'pi pi-users',
    roles: 'pi pi-id-card',
    permissions: 'pi pi-shield',
    settings: 'pi pi-cog',
    reports: 'pi pi-chart-bar',
    patients: 'pi pi-user',
    samples: 'pi pi-flask',
    tests: 'pi pi-microchip',
    results: 'pi pi-check-square',
    orders: 'pi pi-shopping-cart',
    invoices: 'pi pi-file',
    payments: 'pi pi-credit-card',
    hr: 'pi pi-briefcase',
    employees: 'pi pi-user-edit',
    salaries: 'pi pi-dollar',
    inventory: 'pi pi-box',
    suppliers: 'pi pi-truck',
    customers: 'pi pi-users',
    audit: 'pi pi-history',
    logs: 'pi pi-list',
  };

  // system key → theme class
  private static readonly SYSTEM_THEME: Record<string, { icon: string; themeClass: string }> = {
    lab: { icon: 'pi pi-microchip', themeClass: 'theme-lab' },
    curalab: { icon: 'pi pi-microchip', themeClass: 'theme-lab' },
    hr: { icon: 'pi pi-briefcase', themeClass: 'theme-hr' },
    insurance: { icon: 'pi pi-shield', themeClass: 'theme-insurance' },
    auth: { icon: 'pi pi-key', themeClass: 'theme-auth' },
    system: { icon: 'pi pi-server', themeClass: 'theme-system' },
  };

  ngOnInit() {
    this.roleId = this.route.snapshot.paramMap.get('id');
    if (!this.roleId) {
      this.router.navigateByUrl('/roles');
      return;
    }
    this.loadAll();
  }

  private loadAll() {
    this.loading = true;
    Promise.all([
      this.api.listRoles().toPromise(),
      this.api.listAllPermissions(),
    ])
      .then(([roles, allPerms]: [Role[] | undefined, Permission[]]) => {
        this.role = (roles || []).find((r: Role) => r.id === this.roleId) || null;
        if (!this.role) {
          this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'الدور غير موجود' });
          this.router.navigateByUrl('/roles');
          return;
        }

        this.initiallySelected = new Set(this.role.actions || []);
        this.buildGroups(allPerms);
        this.loading = false;
      })
      .catch((err) => {
        console.error(err);
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: 'فشل تحميل البيانات' });
        this.loading = false;
      });
  }

  private buildGroups(perms: Permission[]) {
    const map = new Map<string, ResourceGroup>();
    const systemSet = new Set<string>();

    // If the role is scoped to a single system, restrict the catalog to that system
    // so the picker doesn't drown the user in irrelevant rows.
    const scope = this.role?.systemKey ?? null;

    for (const p of perms) {
      if (!p.systemKey || !p.code) continue;
      if (p.isActive === false) continue;
      if (scope && p.systemKey !== scope) continue;

      systemSet.add(p.systemKey);

      const dotIdx = p.code.indexOf('.');
      const resource = dotIdx === -1 ? p.code : p.code.substring(0, dotIdx);
      const action = dotIdx === -1 ? p.code : p.code.substring(dotIdx + 1);

      const groupKey = `${p.systemKey}::${resource}`;
      let group = map.get(groupKey);
      if (!group) {
        group = {
          system: p.systemKey,
          resource,
          label: this.humanizeResource(resource),
          icon: this.iconForResource(resource),
          actions: [],
          expanded: false,
          matched: true,
        };
        map.set(groupKey, group);
      }

      const key = `${p.systemKey}:${p.code}`;
      group.actions.push({
        key,
        action: action || '·',
        displayNameAr: p.displayNameAr,
        displayNameEn: p.displayNameEn,
        selected: this.initiallySelected.has(key),
      });
    }

    const actionOrder = ['read', 'write', 'delete', 'approve', 'reject', 'pre-approve', 'pre-reject', 'accept', 'invoice', 'transfer', 'deposit', 'withdraw', 'collect', 'send', 'send-bulk'];
    for (const g of map.values()) {
      g.actions.sort((a, b) => {
        const ai = actionOrder.indexOf(a.action);
        const bi = actionOrder.indexOf(b.action);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.action.localeCompare(b.action);
      });
    }

    this.groups = Array.from(map.values()).sort((a, b) => {
      if (a.system !== b.system) return a.system.localeCompare(b.system);
      return a.resource.localeCompare(b.resource);
    });
    this.systems = Array.from(systemSet).sort();

    // Auto-expand groups that already have a selection
    for (const g of this.groups) {
      if (g.actions.some((a) => a.selected)) g.expanded = true;
    }
  }

  private humanizeResource(resource: string): string {
    if (resource === '*') return 'كل الصلاحيات (Wildcard)';
    if (resource === 'access') return 'الوصول العام';
    return resource.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private iconForResource(resource: string): string {
    return RolePermissionsPage.RESOURCE_ICON[resource] || 'pi pi-folder';
  }

  // ============== Visual helpers ==============
  verbTone(action: string): string {
    return RolePermissionsPage.VERB_TONE[action.toLowerCase()] || 'tone-neutral';
  }

  get systemTheme(): { icon: string; themeClass: string } {
    const key = (this.role?.systemKey || '').toLowerCase();
    if (!key) return { icon: 'pi pi-globe', themeClass: 'theme-global' };
    return RolePermissionsPage.SYSTEM_THEME[key] ?? { icon: 'pi pi-th-large', themeClass: 'theme-default' };
  }

  // ============== Filters ==============
  get visibleGroups(): ResourceGroup[] {
    const q = this.search.trim().toLowerCase();
    return this.groups.filter((g) => {
      if (this.systemFilter && g.system !== this.systemFilter) return false;
      if (!q) {
        g.matched = true;
        return true;
      }
      const inGroup =
        g.resource.toLowerCase().includes(q) ||
        g.label.toLowerCase().includes(q) ||
        g.actions.some((a) =>
          a.key.toLowerCase().includes(q) ||
          a.action.toLowerCase().includes(q) ||
          (a.displayNameAr ?? '').toLowerCase().includes(q) ||
          (a.displayNameEn ?? '').toLowerCase().includes(q)
        );
      g.matched = inGroup;
      return inGroup;
    });
  }

  // ============== Group selection helpers ==============
  isGroupAllSelected(g: ResourceGroup): boolean {
    return g.actions.length > 0 && g.actions.every((a) => a.selected);
  }
  isGroupPartial(g: ResourceGroup): boolean {
    const sel = g.actions.filter((a) => a.selected).length;
    return sel > 0 && sel < g.actions.length;
  }
  groupSelectedCount(g: ResourceGroup): number {
    return g.actions.filter((a) => a.selected).length;
  }
  toggleGroup(g: ResourceGroup, value: boolean) {
    for (const a of g.actions) a.selected = value;
  }

  // ============== Global helpers ==============
  get totalSelected(): number {
    let n = 0;
    for (const g of this.groups) for (const a of g.actions) if (a.selected) n++;
    return n;
  }
  get totalAvailable(): number {
    let n = 0;
    for (const g of this.groups) n += g.actions.length;
    return n;
  }
  get totalVisible(): number {
    let n = 0;
    for (const g of this.visibleGroups) n += g.actions.length;
    return n;
  }
  get totalVisibleSelected(): number {
    let n = 0;
    for (const g of this.visibleGroups) for (const a of g.actions) if (a.selected) n++;
    return n;
  }
  get progressPercent(): number {
    if (this.totalAvailable === 0) return 0;
    return Math.round((this.totalSelected / this.totalAvailable) * 100);
  }
  get hasChanges(): boolean {
    const current = new Set<string>();
    for (const g of this.groups) for (const a of g.actions) if (a.selected) current.add(a.key);
    if (current.size !== this.initiallySelected.size) return true;
    for (const k of current) if (!this.initiallySelected.has(k)) return true;
    return false;
  }
  get netDelta(): { added: number; removed: number } {
    const current = new Set<string>();
    for (const g of this.groups) for (const a of g.actions) if (a.selected) current.add(a.key);
    let added = 0;
    let removed = 0;
    for (const k of current) if (!this.initiallySelected.has(k)) added++;
    for (const k of this.initiallySelected) if (!current.has(k)) removed++;
    return { added, removed };
  }

  selectAllVisible() {
    for (const g of this.visibleGroups) this.toggleGroup(g, true);
  }
  clearAllVisible() {
    for (const g of this.visibleGroups) this.toggleGroup(g, false);
  }
  expandAll() {
    for (const g of this.visibleGroups) g.expanded = true;
  }
  collapseAll() {
    for (const g of this.visibleGroups) g.expanded = false;
  }
  resetChanges() {
    for (const g of this.groups) {
      for (const a of g.actions) a.selected = this.initiallySelected.has(a.key);
    }
  }
  clearFilters() {
    this.search = '';
    this.systemFilter = '';
  }

  // ============== Save ==============
  save() {
    if (!this.roleId) return;
    const selected: string[] = [];
    for (const g of this.groups) for (const a of g.actions) if (a.selected) selected.push(a.key);

    this.saving = true;
    this.api.setRoleActions(this.roleId, selected).subscribe({
      next: () => {
        this.saving = false;
        this.initiallySelected = new Set(selected);
        if (this.role) this.role.actions = selected;
        this.ms.add({ severity: 'success', summary: 'تم', detail: 'تم حفظ الصلاحيات' });
      },
      error: (err) => {
        this.saving = false;
        const msg = err?.error?.message || 'فشل الحفظ';
        this.ms.add({ severity: 'error', summary: 'خطأ', detail: msg });
      },
    });
  }

  cancel() {
    this.router.navigateByUrl('/roles');
  }

  trackGroup = (_: number, g: ResourceGroup) => `${g.system}::${g.resource}`;
  trackAction = (_: number, a: ActionEntry) => a.key;
}
